import SEO from "../components/seo.jsx"
import { HT } from "../siteConfig";
import { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ChevronDown } from "lucide-react";
import HeroImg from "../assets/hairline-procedureImg.png";
import ProcedureImg from "../assets/patient-1-in-process(front).jpg"
import BreadcrumbImg from "../assets/hairlineRecon-breadcrumb-bg.jpg";
import SurgeonImg from "../assets/drbhaveshgupta.jpeg";
import TypesImg from "../assets/hairlineRecon-types.jpg";

gsap.registerPlugin(ScrollTrigger);
const cinzel = { fontFamily: "'Cinzel', serif" };

/* ============================================================
   CONTENT pulled straight from the "Hairline Reconstruction" page
============================================================ */
const PAGE = {
  meta: {
    title: `Hairline Reconstruction in ${HT.placeName} | Clinique Internationale`,
    description:
      "Restore a natural, balanced hairline with advanced FUE and FUT techniques at Clinique Internationale, India. Consult our specialists today.",
  },

  hero: {
    eyebrow: "Natural Hairline Design",
    heading: "Hairline Reconstruction",
    sub: "A balanced, natural frontal hairline restored with precision by Dr. Bhavesh Gupta at Clinique Internationale.",
  },

  breadcrumb: [
    { label: "Home", href: "/" },
    { label: "Hairline Reconstruction", href: "/hairline-reconstruction.php" },
  ],

  intro: {
    eyebrow: "Overview",
    heading: `Hairline Reconstruction in ${HT.placeName}`,
    paragraphs: [
      "Hairline reconstruction involves surgically altering the frontal hairline to enhance appearance. This procedure often addresses receding or uneven hairlines through hair transplant or surgical adjustments. By redistributing or implanting hair follicles, it aims to create a more balanced and natural hairline.",
      "It can restore confidence and improve aesthetics, providing a lasting solution for individuals experiencing hairline issues.",
      `Clinique Internationale offers cutting-edge hairline reconstruction services in ${HT.placeName}, employing advanced techniques for natural-looking results. Their skilled team provides personalized treatments tailored to individual needs, ensuring precision and satisfaction.`,
    ],
  },

  surgeon: {
    eyebrow: "Lead Surgeon",
    heading: `Best Hairline Reconstruction Surgeon in ${HT.placeName}`,
    paragraphs: [
      "Clinique Internationale boasts renowned expertise in hairline reconstruction, led by the highly skilled Dr. Bhavesh Gupta. With a sterling reputation, Dr. Gupta stands out for his precision and artistry in hair restoration procedures.",
      "He combines extensive knowledge with cutting-edge technology, offering personalized solutions tailored to individual needs. His compassionate approach and commitment to excellence make him a trusted figure in hairline reconstruction surgery.",
      "Patients benefit not only from his expertise but also from a caring environment that prioritizes their comfort and satisfaction, making their transformative journey a positive and rewarding experience.",
    ],
  },

  cost: {
    eyebrow: "Pricing",
    heading: "Hairline Reconstruction Cost",
    intro:
      `Hairline reconstruction costs at Clinique Internationale in ${HT.placeName} typically range from ₹50,000 to ₹2,00,000, depending on the complexity of the procedure and the extent of reconstruction required. The clinic offers advanced techniques such as FUT and FUE, with cost factoring in surgeon expertise, facilities, the number of grafts, and post-operative care.`,
    outro: "It's advisable to consult with the clinic directly for accurate pricing information.",
    cities: [
     { city: "Pune", surgeon: "Dr. Ajinkya Patil", href: "https://pune.hairclinique.in/" },
{ city: "Bangalore", surgeon: "Dr. Idris MD", href: "https://bangalore.hairclinique.in/" },
{ city: "Mumbai", surgeon: "Dr. Bhavesh Gupta", href: "/" },
      { city: "Shirdi", surgeon: "Dr. Pranay Thakur", href: "https://shirdi.hairclinique.in/" },
    ],
  },

  whyChoose: {
    eyebrow: "The Difference",
    heading: "Why Choose Clinique Internationale",
    intro: "Clinique Internationale is a leading choice for hairline reconstruction for several reasons.",
    items: [
      { title: "Expertise", desc: "A team of skilled and experienced professionals specializing in hair restoration techniques, ensuring a high level of expertise." },
      { title: "Advanced Techniques", desc: "The clinic utilizes advanced hair transplant methods and technologies to achieve natural-looking and long-lasting results." },
      { title: "Customized Approach", desc: "Personalized treatment plans tailored to individual needs, considering hair type, scalp condition, and desired outcomes." },
      { title: "Reputation", desc: "A strong track record and positive reviews from satisfied patients, reflecting reliability and success in hair restoration." },
      { title: "Patient Care", desc: "Priority on patient comfort, care, and safety throughout the entire process, ensuring a positive experience." },
    ],
  },

  types: {
    eyebrow: "Know Your Shape",
    heading: "Types of Hairlines",
    intro: "Understanding your natural hairline shape helps guide a design that looks balanced and authentic.",
    items: [
      { num: "01", title: "Straight Hairline", desc: "A uniform and even hairline across the forehead without any recessions or peaks." },
      { num: "02", title: "Widow's Peak", desc: "A V-shaped point in the center of the forehead where the hairline forms a distinctive downward point." },
      { num: "03", title: "M-Shaped / Receding", desc: "A gradual recession of the hairline, often forming an 'M' shape as it recedes from the temples." },
      { num: "04", title: "Round Hairline", desc: "A curved hairline without any prominent points or recessions, creating a soft, rounded shape." },
      { num: "05", title: "Uneven Hairline", desc: "An irregular hairline that may not follow a distinct pattern, with varying heights across the forehead." },
      { num: "06", title: "High Hairline", desc: "A hairline that starts higher up on the forehead, giving the appearance of a larger forehead area." },
      { num: "07", title: "Low Hairline", desc: "A hairline that starts closer to the eyebrows, creating the appearance of a smaller forehead." },
    ],
  },

  faqs: {
    eyebrow: "Common Questions",
    heading: "Hairline Reconstruction FAQ",
    items: [
      {
        q: "Where can I get the best treatment for hairline reconstruction?",
        a: "For advanced and reliable hairline reconstruction, Clinique Internationale, Mumbai is a top choice. The clinic offers modern FUE and FUT techniques, a patient-focused approach, and natural-looking, long-lasting results.",
      },
      {
        q: "How is the procedure performed?",
        a: "At Clinique Internationale, hairline reconstruction is done using FUE or FUT techniques. Hair follicles are carefully transplanted to recreate a natural hairline that complements the patient's facial structure.",
      },
      {
        q: "How long does it take to see results?",
        a: "Initial hair growth begins within 3–4 months. Full, permanent results with a natural-looking hairline are typically visible in 9–12 months.",
      },
      {
        q: "What is the cost of hairline reconstruction?",
        a: "The cost depends on the number of grafts required, the complexity of the hairline design, and the technique used. A consultation at Clinique Internationale will provide a personalized estimate.",
      },
      {
        q: "How should I care for my hairline after the procedure?",
        a: "Post-procedure care includes avoiding touching or scratching the transplanted area, protecting the scalp from direct sunlight, taking all prescribed medications, and attending follow-up appointments to monitor healing and progress.",
      },
    ],
  },

  cta: {
    eyebrow: "Take the First Step",
    heading: "Ready for a Natural, Balanced Hairline?",
    sub: "Book a personalised consultation with Dr. Bhavesh Gupta at Clinique Internationale and take the first step toward natural, lasting results.",
    primary: { label: "Book Consultation", href: "https://api.whatsapp.com/send?phone=919082123009&text=I%27m%20interested%20in%20Hair%20Restoration" },
    secondary: { label: "View All Treatments", href: "/hairline-reconstruction.php" },
  },
};

/* ============================================================
   REUSABLE SUB-COMPONENTS
============================================================ */
function Eyebrow({ children, light = false, align = "left" }) {
  return (
    <p
      style={cinzel}
      className={`text-[10.5px] tracking-[0.28em] uppercase mb-3 flex items-center gap-2.5 ${
        align === "center" ? "justify-center" : "justify-start"
      } ${light ? "text-[#c9a84c]/70" : "text-[#a07828]"}`}
    >
      <span className={`w-6 h-px ${light ? "bg-[#c9a84c]/50" : "bg-[#c9a84c]"}`} />
      {children}
    </p>
  );
}

function SectionHeading({ children, light = false, align = "left" }) {
  return (
    <h2
      style={cinzel}
      className={`font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.85rem] uppercase tracking-[0.05em] mb-8 ${
        align === "center" ? "text-center" : "text-left"
      } ${light ? "text-white" : "text-[#1a0f02]"}`}
    >
      {children}
    </h2>
  );
}

function GoldDivider() {
  return <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />;
}

function FramedImage({ src, alt, className = "" }) {
  return (
    <div className={`relative rounded-2xl overflow-hidden border border-[#c9a84c]/25 ${className}`}>
      <img src={src} alt={alt} className="w-full h-full object-cover" />
      <span className="absolute inset-0 ring-1 ring-inset ring-[#c9a84c]/15 rounded-2xl pointer-events-none" />
    </div>
  );
}

function FaqItem({ faq, index, openIndex, setOpenIndex }) {
  const isOpen = openIndex === index;
  return (
    <div className="border border-[#c9a84c]/15 rounded-xl overflow-hidden bg-white/[0.03]">
      <button
        onClick={() => setOpenIndex(isOpen ? null : index)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-[#c9a84c]/[0.06] transition-colors"
      >
        <span style={cinzel} className="text-[12.5px] sm:text-[13px] font-bold tracking-[0.02em] text-white/90">
          {String(index + 1).padStart(2, "0")}. {faq.q}
        </span>
        <ChevronDown
          size={16}
          className={`flex-shrink-0 text-[#c9a84c] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className="grid transition-all duration-300 ease-in-out"
        style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <p className="px-5 pb-4 text-[13px] leading-[1.8] text-white/55">{faq.a}</p>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PAGE COMPONENT
============================================================ */
export default function HairlineReconstruction({ url = "https://www.hairclinique.in/hairline-reconstruction.php" }) {
  const pageRef = useRef(null);
  const heroEyeRef = useRef(null);
  const heroHeadRef = useRef(null);
  const heroSubRef = useRef(null);
  const sectionRefs = useRef([]);
  const addSection = (el) => el && !sectionRefs.current.includes(el) && sectionRefs.current.push(el);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";
      gsap.fromTo(heroEyeRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ease });
      gsap.fromTo(heroHeadRef.current, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.75, ease, delay: 0.1 });
      gsap.fromTo(heroSubRef.current, { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: 0.7, ease, delay: 0.18 });

      sectionRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(
          el,
          { opacity: 0, y: 36 },
          {
            opacity: 1,
            y: 0,
            duration: 0.75,
            ease,
            scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none reverse" },
          }
        );
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  /* full-bleed wrapper: fills the viewport with generous edge padding instead of a centered max-width column */
  const wrap = "w-full px-5 sm:px-10 lg:px-16 xl:px-24";
  const sec = "py-16 sm:py-20 md:py-24";

  return (
    <>
    <SEO
  title={`Hairline Reconstruction in ${HT.placeName} | Clinique Internationale`}
  description={`Restore a natural hairline with expert Hairline Reconstruction in ${HT.placeName} at Clinique Internationale, led by Dr. Bhavesh Gupta. Precision techniques, natural results.`}
  url={url}
/>
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap"
        rel="stylesheet"
      />

<div
  ref={pageRef}
  className="relative w-full bg-[#f2ece0] overflow-hidden mt-16 lg:mt-20"
  style={{ fontFamily: "'Raleway', sans-serif" }}
>
  {/* ══════════════════════════════════════
      HERO full width
  ══════════════════════════════════════ */}
  <div className="relative w-full h-[200px] xs:h-[280px] sm:h-[340px] md:h-[700px] flex items-center justify-end overflow-hidden">
    <img className="absolute inset-0 w-full h-full object-cover object-[center_20%] md:object-center" src={HeroImg} alt={PAGE.hero.heading} />
    <div className="absolute inset-0" />
    <div
      className="absolute inset-0 opacity-40 pointer-events-none"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")",
      }}
    />
    <div className="relative z-10 text-right px-4 sm:px-10 md:px-16 max-w-[85%] sm:max-w-xl ml-auto mr-0">
      <p ref={heroEyeRef} style={cinzel} className="text-[8px] xs:text-[9px] sm:text-[10.5px] tracking-[0.2em] xs:tracking-[0.24em] sm:tracking-[0.32em] uppercase text-[#c9a84c] mb-2 sm:mb-4 flex items-center justify-end gap-2 sm:gap-3">
        <span className="w-4 sm:w-7 h-px bg-[#c9a84c]" />
        {PAGE.hero.eyebrow}
        <span className="w-4 sm:w-7 h-px bg-[#c9a84c]" />
      </p>
      <h1
        ref={heroHeadRef}
        style={cinzel}
        className="font-black text-[1.4rem] xs:text-[1.8rem] sm:text-[2.8rem] md:text-[3.4rem] tracking-[0.02em] sm:tracking-[0.04em] uppercase text-white mb-2 sm:mb-3 leading-[1.15] sm:leading-tight"
      >
        {PAGE.hero.heading}
      </h1>
      <p ref={heroSubRef} className="text-[11px] xs:text-[12px] sm:text-[15px] text-white/70 leading-relaxed ml-auto">
        {PAGE.hero.sub}
      </p>
    </div>
  </div>

        {/* ── BREADCRUMB full width with background image ── */}
        <div className="relative w-full overflow-hidden">
          <img className="absolute inset-0 w-full h-full object-cover" src={BreadcrumbImg} alt="" aria-hidden="true" />
          <div className="absolute inset-0 bg-[#0e0905]/88" />
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/25 to-transparent" />
          <div className={`relative ${wrap} py-3.5 flex flex-wrap items-center gap-2`}>
            {PAGE.breadcrumb.map((b, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <span className="text-[#c9a84c]/30 text-xs">›</span>}
                {b.href ? (
                  <a href={b.href} style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]/60 hover:text-[#c9a84c] transition-colors">
                    {b.label}
                  </a>
                ) : (
                  <span style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]">
                    {b.label}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════
            INTRO
        ══════════════════════════════════════ */}
<div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <FramedImage src={ProcedureImg} alt="Hair transplant procedure close-up" className="h-[260px] sm:h-[320px] md:h-[380px] order-last md:order-first" />
            <div>
              <Eyebrow>{PAGE.intro.eyebrow}</Eyebrow>
              <SectionHeading>{PAGE.intro.heading}</SectionHeading>
              {PAGE.intro.paragraphs.map((p, i) => (
                <p key={i} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] mb-4">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            SURGEON (dark band, text + image)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <Eyebrow light>{PAGE.surgeon.eyebrow}</Eyebrow>
                <SectionHeading light>{PAGE.surgeon.heading}</SectionHeading>
                {PAGE.surgeon.paragraphs.map((p, i) => (
                  <p key={i} className="text-[13.5px] leading-[1.9] text-white/60 mb-4">
                    {p}
                  </p>
                ))}
              </div>
              <img src={SurgeonImg} alt="Dr. Bhavesh Gupta, hairline reconstruction surgeon" className="h-[260px] sm:h-[320px] md:h-[460px] rounded-2xl" />
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            COST BY CITY / CENTERS
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection}>
            <Eyebrow align="center">{PAGE.cost.eyebrow}</Eyebrow>
            <SectionHeading align="center">{PAGE.cost.heading}</SectionHeading>
            <p className="text-[14.5px] leading-[1.9] text-[#3a2e1e] max-w-3xl mx-auto text-center mb-10">{PAGE.cost.intro}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto mb-8">
              {PAGE.cost.cities.map((c, i) => (
                <a
                  key={i}
                  href={c.href}
                  className="bg-white border border-[#c9a84c]/18 rounded-2xl p-6 relative overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_12px_32px_rgba(90,60,10,0.1)] transition-all duration-300 block text-center group"
                >
                  <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                  <div style={cinzel} className="text-[13px] font-bold tracking-[0.06em] uppercase text-[#1a0f02] mb-3 group-hover:text-[#a07828] transition-colors">
                    {c.city}
                  </div>
                  <div
                    style={cinzel}
                    className="font-black text-[1.05rem] leading-snug bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] bg-clip-text text-transparent"
                  >
                    {c.surgeon}
                  </div>
                </a>
              ))}
            </div>
            <p className="text-[13px] leading-[1.85] text-[#6b5535] italic max-w-3xl mx-auto text-center">{PAGE.cost.outro}</p>
          </div>
        </div>

        {/* ══════════════════════════════════════
            WHY CHOOSE (benefits-style grid)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow light align="center">{PAGE.whyChoose.eyebrow}</Eyebrow>
              <SectionHeading light align="center">{PAGE.whyChoose.heading}</SectionHeading>
              <p className="text-[14.5px] leading-[1.9] text-white/55 max-w-3xl mx-auto text-center mb-10">{PAGE.whyChoose.intro}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {PAGE.whyChoose.items.map((b, i) => (
                  <div
                    key={i}
                    className="relative overflow-hidden bg-white/[0.04] border border-[#c9a84c]/15 rounded-2xl p-5 hover:bg-[#c9a84c]/[0.07] hover:border-[#c9a84c]/30 transition-colors"
                  >
                    <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/35 to-transparent" />
                    <div className="flex items-center gap-2.5 mb-2">
                      <span style={cinzel} className="text-[11px] font-black text-[#c9a84c]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div style={cinzel} className="text-[11.5px] font-bold tracking-[0.05em] uppercase text-white/90">
                        {b.title}
                      </div>
                    </div>
                    <p className="text-[12.5px] leading-[1.75] text-white/55">{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            TYPES OF HAIRLINES (image + cards)
        ══════════════════════════════════════ */}
        <div className="bg-[#faf8f4] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow align="center">{PAGE.types.eyebrow}</Eyebrow>
              <SectionHeading align="center">{PAGE.types.heading}</SectionHeading>
              <p className="text-[14.5px] leading-[1.9] text-[#3a2e1e] max-w-3xl mx-auto text-center mb-10">{PAGE.types.intro}</p>

              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-10 items-start">
                <FramedImage src={TypesImg} alt="Natural hairline design close-up" className="h-[260px] sm:h-[340px] lg:h-full lg:sticky lg:top-24" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {PAGE.types.items.map((t) => (
                    <div key={t.num} className="bg-white border border-[#c9a84c]/18 rounded-2xl p-5 relative overflow-hidden hover:shadow-[0_8px_28px_rgba(90,60,10,0.08)] transition-shadow">
                      <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                      <div className="flex gap-4 items-start">
                        <span style={cinzel} className="font-black text-[1.5rem] leading-none text-[#c9a84c]/20 select-none flex-shrink-0">
                          {t.num}
                        </span>
                        <div>
                          <div style={cinzel} className="text-[12px] font-bold tracking-[0.05em] uppercase text-[#1a0f02] mb-1.5">
                            {t.title}
                          </div>
                          <p className="text-[12.5px] leading-[1.75] text-[#6b5535]">{t.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            FAQ (dark band accordion)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection} className="max-w-3xl mx-auto">
              <Eyebrow light align="center">{PAGE.faqs.eyebrow}</Eyebrow>
              <SectionHeading light align="center">{PAGE.faqs.heading}</SectionHeading>
              <div className="flex flex-col gap-3">
                {PAGE.faqs.items.map((faq, i) => (
                  <FaqItem key={i} faq={faq} index={i} openIndex={openFaq} setOpenIndex={setOpenFaq} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            CTA
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="max-w-xl mx-auto text-center">
            <Eyebrow align="center">{PAGE.cta.eyebrow}</Eyebrow>
            <h3
              style={cinzel}
              className="font-black text-[1.3rem] sm:text-[1.65rem] md:text-[1.9rem] text-[#1a0f02] uppercase tracking-[0.06em] mb-3.5"
            >
              {PAGE.cta.heading}
            </h3>
            <p className="text-[14px] text-[#6b5535] leading-[1.75] mb-8">{PAGE.cta.sub}</p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a
                href={PAGE.cta.primary.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...cinzel, backgroundImage: "linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)", backgroundSize: "200% 100%" }}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-3.5 rounded-md text-[#1a0f02] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)] transition-all inline-block"
              >
                {PAGE.cta.primary.label}
              </a>
              <a
                href={PAGE.cta.secondary.href}
                style={cinzel}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-[13px] rounded-md bg-transparent border border-[#1a0f02]/35 text-[#1a0f02] hover:-translate-y-0.5 hover:border-[#c9a84c] hover:text-[#a07828] transition-all inline-block"
              >
                {PAGE.cta.secondary.label}
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}